import { useReactiveVar } from '@apollo/client';

import { Role } from '@/routes/PrivateRoute';
import {
  CurrentCustomerAdminFragment,
  CurrentTenantAdminFragment,
} from '@/types/graphql';
import { currentAdminVar, currentAgencyVar } from '@/util/apollo/cache';

// Typeguard to ensure `customer` is a property of the currentAdmin object.
// Defined at module scope, not inside the hook: it closes over nothing, and a
// fresh identity per render defeats every useMemo/useEffect that lists it as a
// dependency. See GRAVY-10366.
const isCurrentCustomerAdmin = (
  arg: CurrentCustomerAdminFragment | CurrentTenantAdminFragment | null,
): arg is CurrentCustomerAdminFragment =>
  (arg as CurrentCustomerAdminFragment).customer !== undefined;

const useAuth = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);

  const currentAdmin = useReactiveVar(currentAdminVar);
  const currentAdminIsCustomerAdmin =
    currentAdmin?.role === Role.CUSTOMER_ADMIN;
  // Strict tenant-admin check (helpdesk admins excluded too) — gates
  // GravyWork-internal cost signals like the OT risk banner and the
  // "Hours This Week" staffing column.
  const currentAdminIsTenantAdmin = currentAdmin?.role === Role.TENANT_ADMIN;

  return {
    currentAdmin,
    currentAdminIsCustomerAdmin,
    currentAdminIsTenantAdmin,
    isCurrentCustomerAdmin,
    currentAgency,
  };
};

export default useAuth;
